import { AppShellListingCardProvider } from "./listing-card-composition";
import { scrubDogCardOpenedCapture } from "@/features/listings";
import { capturePageview } from "@/lib/core/posthog-pageview";
import {
    capturePreparedShortLinkArrival,
    prepareShortLinkArrival,
    scrubShortLinkArrivalCapture,
} from "@/lib/core/short-links/short-link-arrival";
import { redactContactTokenPaths } from "@/lib/core/short-links/short-link-attribution";
import posthog, {
    type CaptureResult,
    type PostHogInterface,
    type Properties,
} from "posthog-js";
import { PostHogProvider } from "@/lib/core/deferred-posthog-react";
import type { ReactNode } from "react";
import { SkulletProvider } from "skullet/react";
import {
    AuthAnalytics,
    ResponsiveAuthProvider,
} from "@/features/account-access";
import {
    hasSearchPrefetchActivity,
    installSearchPrefetchPageLifecycle,
    searchPrefetchTracker,
    scrubSearchSubmittedCapture,
} from "@/features/discovery";

const analyticsEnabled =
    import.meta.env.PROD ||
    import.meta.env.VITE_PUBLIC_ENABLE_ANALYTICS === "true";

const POSTHOG_API_HOST = "https://us.i.posthog.com";
const POSTHOG_UI_HOST = "https://us.posthog.com";

function withoutContactTokens(properties: Properties): Properties {
    return Object.fromEntries(
        Object.entries(properties).map(([key, value]) => [
            key,
            typeof value === "string" ? redactContactTokenPaths(value) : value,
        ]),
    );
}

function scrubAnalyticsCapture(
    capture: CaptureResult | null,
): CaptureResult | null {
    if (!capture) return capture;
    const safeCapture = scrubShortLinkArrivalCapture(
        scrubDogCardOpenedCapture(scrubSearchSubmittedCapture(capture)),
    );
    if (!safeCapture) return safeCapture;
    return {
        ...safeCapture,
        properties: withoutContactTokens(safeCapture.properties),
        ...(safeCapture.$set
            ? { $set: withoutContactTokens(safeCapture.$set) }
            : {}),
        ...(safeCapture.$set_once
            ? { $set_once: withoutContactTokens(safeCapture.$set_once) }
            : {}),
    };
}

const posthogOptions = {
    api_host: POSTHOG_API_HOST,
    ui_host: POSTHOG_UI_HOST,
    capture_pageview: false,
    capture_pageleave: true,
    before_send: scrubAnalyticsCapture,
    opt_out_capturing_by_default: !analyticsEnabled,
    disable_session_recording: !analyticsEnabled,

    session_recording: {
        blockSelector: "[data-ph-no-capture]",
        captureCanvas: {
            recordCanvas: false,
        },
    },
    loaded: (posthog: PostHogInterface) => {
        posthog.register({ environment: import.meta.env.MODE });
        posthog.unregister("listing_more_dogs_arm");
        posthog.unregister("photo_first_cards_arm");
        capturePageview(window.location.pathname);
        capturePreparedShortLinkArrival({
            analyticsEnabled,
            analytics: posthog,
        });
    },
} as const;

if (typeof window !== "undefined") {
    prepareShortLinkArrival({
        href: window.location.href,
        replaceState: (canonicalHref) => {
            window.history.replaceState(
                window.history.state,
                "",
                canonicalHref,
            );
        },
    });
}

if (typeof window !== "undefined") {
    installSearchPrefetchPageLifecycle(
        window,
        searchPrefetchTracker,
        (summary) => {
            if (hasSearchPrefetchActivity(summary)) {
                posthog.capture("search_prefetch_session_summary", summary);
            }
        },
    );
}

export function AppShellProviders({ children }: { children: ReactNode }) {
    return (
        <PostHogProvider
            apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
            options={posthogOptions}
        >
            <ResponsiveAuthProvider>
                <AppShellListingCardProvider>
                    <SkulletProvider>{children}</SkulletProvider>
                </AppShellListingCardProvider>
            </ResponsiveAuthProvider>
        </PostHogProvider>
    );
}

export { AuthAnalytics as AppShellAuthAnalytics };
